import { useEffect, useState, useCallback, useRef } from "react";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Factory, Gear, Tag, Ruler, UserGear, BuildingOffice, Users as UsersIcon, ShareNetwork,
  Plus, Trash, PencilSimple, Check, X, ArrowUp, ArrowDown, Lock, Warning, DownloadSimple, ArrowsClockwise,
  Broom, LockKey, LockOpen, ShieldCheck,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { API, useProcesses } from "../App";

const TABS = [
  // Processes moved to Process Master (Feb 2026 restructure). See /process-master.
  { id: "outsource",  label: "Outsource Procs", icon: ShareNetwork },
  { id: "roles",      label: "Roles",           icon: UserGear },
  { id: "users",      label: "Users",           icon: UsersIcon },
  { id: "uom",        label: "Units",           icon: Ruler },
  { id: "rm",         label: "RM Code Format",  icon: Tag },
  { id: "customers",  label: "Customer Cleanup",icon: Broom },
  { id: "company",    label: "Company Branding",icon: BuildingOffice },
  { id: "passwords",  label: "Passkeys",        icon: Lock },
  { id: "backup",     label: "Backup & Storage",icon: BuildingOffice },
  { id: "recovery",   label: "Data Recovery",   icon: Warning },
  { id: "neg-fix",    label: "Fix Negatives",   icon: Warning },
  { id: "mtr",        label: "Run MTR",         icon: ArrowsClockwise },
];

export default function Customization() {
  const [tab, setTab] = useState("outsource");
  return (
    <Layout>
      <div className="p-6 space-y-5" data-testid="customization-page">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>
            Customization
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Configure per-company processes, roles, units, and branding.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-[#E2E8F0] pb-2">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            const isMtr = t.id === "mtr";
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-4 py-2 rounded-t-md text-xs font-bold uppercase tracking-wide flex items-center gap-2 transition-all ${
                  active
                    ? isMtr ? "bg-[#B91C1C] text-white ring-2 ring-[#B91C1C]/40"
                            : "bg-[#1D2951] text-[#F5DF4D]"
                    : isMtr ? "bg-[#B91C1C]/10 text-[#B91C1C] hover:bg-[#B91C1C]/20 border-2 border-[#B91C1C]/40"
                            : "bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]"
                }`}
              >
                <Icon size={14} weight={isMtr ? "bold" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "outsource" && <OutsourceProcessesPanel />}
        {tab === "roles" && <RolesPanel />}
        {tab === "users" && <UsersPanel />}
        {tab === "uom" && <UomPanel />}
        {tab === "rm" && <RmTemplatePanel />}
        {tab === "customers" && <CustomerCleanupPanel />}
        {tab === "company" && <CompanyPanel />}
        {tab === "passwords" && <PasskeysGate><PasswordsPanel /></PasskeysGate>}
        {tab === "backup" && <BackupPanel />}
        {tab === "recovery" && <DataRecoveryPanel />}
        {tab === "neg-fix" && <NegativeFixPanel />}
        {tab === "mtr" && <RunMTRPanel />}
      </div>
    </Layout>
  );
}


/* ──────────────────────────── Passkeys Gate ──────────────────────────── */
/**
 * Year-based unlock for the entire Passkeys tab.
 * Password format is `Swami@YYYY` (current calendar year).
 * State cached in `sessionStorage` so the gate doesn't re-prompt on every tab switch.
 */
function PasskeysGate({ children }) {
  const expectedPwd = `Swami@${new Date().getFullYear()}`;
  const STORAGE_KEY = "passkeys_tab_unlocked";
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === expectedPwd; }
    catch { return false; }
  });
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (pwd.trim() === expectedPwd) {
      try { sessionStorage.setItem(STORAGE_KEY, expectedPwd); } catch {/* ignore */}
      setUnlocked(true);
      setErr("");
    } else {
      setErr("Wrong password. Contact your administrator if you don't have it.");
    }
  };

  if (unlocked) return children;

  return (
    <Card className="p-8 border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC]" data-testid="passkeys-gate">
      <div className="flex flex-col md:flex-row items-center gap-6 max-w-3xl mx-auto">
        <div className="w-16 h-16 rounded-full bg-[#1D2951] flex items-center justify-center shrink-0">
          <Lock size={28} weight="bold" className="text-white" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-lg font-black text-[#1D2951]">Passkeys — Restricted</h3>
          <p className="text-xs text-[#64748B] mt-1">
            All system, operation and section passwords live behind this gate.
            Enter the annual master password to view or edit them.
          </p>
        </div>
        <form onSubmit={submit} className="flex items-end gap-2 shrink-0 w-full md:w-auto">
          <div className="flex-1 md:w-56">
            <Label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Access Password</Label>
            <Input type="password" value={pwd}
              onChange={e => { setPwd(e.target.value); setErr(""); }}
              placeholder="Enter access password"
              className="h-10 mt-1" data-testid="passkeys-gate-pwd" autoFocus />
          </div>
          <Button type="submit" className="bg-[#1D2951] hover:bg-[#2A3B73] h-10" data-testid="passkeys-gate-unlock">
            Unlock
          </Button>
        </form>
      </div>
      {err && <p className="text-xs text-[#EF4444] mt-3 text-center" data-testid="passkeys-gate-err">{err}</p>}
    </Card>
  );
}




/* ProcessesPanel removed Feb 2026 — moved to /process-master. */

/* ──────────────────────────── Roles ──────────────────────────── */

function RolesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", permissions: "" });
  const [adding, setAdding] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/roles`);
      setRows(res.data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    const perms = form.permissions.split(",").map(s => s.trim()).filter(Boolean);
    try {
      await axios.post(`${API}/roles`, { name: form.name, permissions: perms });
      toast.success("Role created"); setAdding(false);
      setForm({ name: "", permissions: "" });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete role '${r.name}'?`)) return;
    try { await axios.delete(`${API}/roles/${r.id}`); toast.success("Deleted"); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="roles-panel">
      <div className="bg-[#1D2951] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserGear size={18} className="text-[#F5DF4D]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Roles & Permissions</h2>
          <span className="bg-[#F5DF4D] text-[#1D2951] text-xs font-bold rounded-full px-2 py-0.5">{rows.length}</span>
        </div>
        <Button size="sm" onClick={() => setAdding(!adding)} className="bg-[#F5DF4D] hover:bg-[#EAD63A] text-[#1D2951] text-xs font-bold gap-1 h-8" data-testid="add-role-btn">
          <Plus size={14} /> Add Role
        </Button>
      </div>
      {adding && (
        <div className="p-4 bg-[#F0FDF4] border-b border-[#BBF7D0] grid grid-cols-[1fr_2fr_auto_auto] gap-3 items-end" data-testid="add-role-form">
          <Field label="Role Name" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="role-name" placeholder="e.g. QA Inspector" />
          <Field label="Permissions (comma-separated)" value={form.permissions} onChange={v => setForm({ ...form, permissions: v })} testid="role-perms"
            placeholder="e.g. parts.read, transactions.*" />
          <Button onClick={save} className="bg-[#059669] hover:bg-[#047857] text-white h-9 px-4 text-xs font-bold" data-testid="save-role-btn">
            <Check size={14} className="mr-1" /> Save
          </Button>
          <Button variant="outline" onClick={() => setAdding(false)} className="h-9 px-3"><X size={14} /></Button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F1F5F9] text-xs uppercase tracking-wider text-[#64748B]">
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Permissions</th>
              <th className="px-4 py-2 text-center w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-[#E2E8F0]" data-testid={`role-row-${r.name}`}>
                <td className="px-4 py-2.5 font-bold text-[#1D2951]">{r.name}</td>
                <td className="px-4 py-2.5 text-xs text-[#475569]">
                  {(r.permissions || []).map(p => (
                    <code key={p} className="mr-1 bg-[#F1F5F9] px-1.5 py-0.5 rounded">{p}</code>
                  ))}
                </td>
                <td className="px-4 py-2.5 text-center">
                  {["Creator", "Admin", "Worker"].includes(r.name)
                    ? <span className="text-xs text-[#94A3B8]">Protected</span>
                    : <Button size="sm" variant="outline" onClick={() => del(r)}
                        className="h-7 px-2 text-xs text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626] hover:text-white">
                        <Trash size={12} />
                      </Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


/* ──────────────────────────── UOM ──────────────────────────── */

function UomPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", is_default: false });
  const [adding, setAdding] = useState(false);

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/uom`); setRows(r.data || []); }
    catch { toast.error("Failed to load"); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!form.code || !form.name) { toast.error("Code and name required"); return; }
    try {
      await axios.post(`${API}/uom`, form);
      toast.success("UOM created"); setAdding(false);
      setForm({ code: "", name: "", is_default: false });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete UOM '${r.code}'?`)) return;
    try { await axios.delete(`${API}/uom/${r.id}`); toast.success("Deleted"); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="uom-panel">
      <div className="bg-[#1D2951] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ruler size={18} className="text-[#F5DF4D]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Units of Measurement</h2>
        </div>
        <Button size="sm" onClick={() => setAdding(!adding)} className="bg-[#F5DF4D] hover:bg-[#EAD63A] text-[#1D2951] text-xs font-bold gap-1 h-8" data-testid="add-uom-btn">
          <Plus size={14} /> Add UOM
        </Button>
      </div>
      {adding && (
        <div className="p-4 bg-[#F0FDF4] border-b border-[#BBF7D0] grid grid-cols-[1fr_2fr_auto_auto_auto] gap-3 items-end">
          <Field label="Code" value={form.code} onChange={v => setForm({ ...form, code: v.toUpperCase() })} testid="uom-code" placeholder="e.g. LTR" />
          <Field label="Display Name" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="uom-name" placeholder="e.g. Liter" />
          <label className="flex items-center gap-2 text-xs font-bold text-[#475569] h-9">
            <input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} />
            Default
          </label>
          <Button onClick={save} className="bg-[#059669] hover:bg-[#047857] text-white h-9 px-4 text-xs font-bold" data-testid="save-uom-btn">Save</Button>
          <Button variant="outline" onClick={() => setAdding(false)} className="h-9 px-3"><X size={14} /></Button>
        </div>
      )}
      <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        {rows.map(r => (
          <div key={r.id} className="border border-[#E2E8F0] rounded-lg p-3 flex items-center justify-between" data-testid={`uom-${r.code}`}>
            <div>
              <div className="text-xs font-bold text-[#1D2951]">{r.code} {r.is_default && <span className="text-[10px] text-[#059669]">(default)</span>}</div>
              <div className="text-xs text-[#64748B]">{r.name}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => del(r)}
              className="h-7 px-2 text-xs text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626] hover:text-white">
              <Trash size={12} />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}


/* ──────────────────────────── RM Template ──────────────────────────── */

function RmTemplatePanel() {
  const [template, setTemplate] = useState("");
  const [loading, setLoading] = useState(true);
  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/rm-code-template`); setTemplate(r.data.template || ""); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    try { await axios.put(`${API}/rm-code-template`, { template }); toast.success("Template updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="rm-template-panel">
      <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
        <Tag size={18} className="text-[#F5DF4D]" />
        <h2 className="text-sm font-bold text-white uppercase tracking-wider">RM Code Format</h2>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <Label className="text-[11px] font-semibold">Template</Label>
          <Input value={template} onChange={e => setTemplate(e.target.value)} className="min-h-[40px] font-mono text-sm" data-testid="rm-template-input" />
          <p className="text-[11px] text-[#64748B] mt-2">
            Supported variables: <code className="bg-[#F1F5F9] px-1 rounded">{'{type_prefix}'}</code>{' '}
            <code className="bg-[#F1F5F9] px-1 rounded">{'{dim}'}</code>{' '}
            <code className="bg-[#F1F5F9] px-1 rounded">{'{rm_grade}'}</code>{' '}
            <code className="bg-[#F1F5F9] px-1 rounded">{'{rm_suffix}'}</code>
          </p>
        </div>
        <Button onClick={save} className="bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold" data-testid="save-rm-template-btn">
          <Check size={14} className="mr-1" /> Save Template
        </Button>
      </div>
    </Card>
  );
}


/* ──────────────────────────── Company Branding ──────────────────────────── */

function CompanyPanel() {
  const [info, setInfo] = useState(null);
  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/company/info`); setInfo(r.data); }
    catch { toast.error("Failed to load"); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    try {
      const dp = parseInt(info.decimal_precision, 10);
      await axios.put(`${API}/company/info`, {
        name: info.name, logo_url: info.logo_url, accent_color: info.accent_color,
        decimal_precision: Number.isFinite(dp) ? Math.max(0, Math.min(8, dp)) : 2,
      });
      toast.success("Company info updated");
      window.dispatchEvent(new Event("company:changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resetSetup = async () => {
    if (!window.confirm("Reset the setup wizard for this company? Next load will show the setup page before login.")) return;
    try {
      await axios.post(`${API}/company/reset-setup`);
      toast.success("Setup reset — reloading…");
      setTimeout(() => { localStorage.removeItem("token"); window.location.replace("/setup"); }, 800);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!info) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="company-panel">
      <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
        <BuildingOffice size={18} className="text-[#F5DF4D]" />
        <h2 className="text-sm font-bold text-white uppercase tracking-wider">Company Branding</h2>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Company Name" value={info.name || ""} onChange={v => setInfo({ ...info, name: v })} testid="company-name" />
        <Field label="Logo URL" value={info.logo_url || ""} onChange={v => setInfo({ ...info, logo_url: v })} testid="company-logo" />
        <div>
          <Label className="text-[11px] font-semibold">Accent Color</Label>
          <div className="flex items-center gap-2">
            <Input type="color" value={info.accent_color || "#1D2951"} onChange={e => setInfo({ ...info, accent_color: e.target.value })} className="w-16 h-10 p-1" />
            <Input value={info.accent_color || ""} onChange={e => setInfo({ ...info, accent_color: e.target.value })} className="min-h-[40px] flex-1 font-mono text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] font-semibold">Tenant (Company) ID</Label>
          <Input value={info.company_id || ""} disabled className="min-h-[40px] font-mono text-xs bg-[#F1F5F9]" />
          <p className="text-[11px] text-[#64748B] mt-1">Assigned by Swami Hub — read only.</p>
        </div>
        <div>
          <Label className="text-[11px] font-semibold">Backend Decimal Precision</Label>
          <Input
            type="number" min="0" max="8" step="1"
            value={info.decimal_precision ?? 2}
            onChange={e => setInfo({ ...info, decimal_precision: e.target.value })}
            className="min-h-[40px] font-mono"
            data-testid="decimal-precision-input"
          />
          <p className="text-[11px] text-[#64748B] mt-1">
            How many decimal places to STORE (0–8). The UI always displays max 2 decimals
            for readability. Default: 2. Increase if you need finer-grained Kg/value tracking.
          </p>
        </div>
        <div className="md:col-span-2 flex items-center justify-between pt-3 border-t border-[#E2E8F0]">
          <Button onClick={save} className="bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold" data-testid="save-company-btn">
            <Check size={14} className="mr-1" /> Save Branding
          </Button>
          <Button variant="outline" onClick={resetSetup}
            className="text-xs text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626] hover:text-white"
            data-testid="reset-setup-btn">
            <X size={14} className="mr-1" /> Reset Setup Wizard
          </Button>
        </div>
      </div>
    </Card>
  );
}


/* ──────────────────────────── Passkeys ──────────────────────────── */

function PasswordsPanel() {
  const [passwords, setPasswords] = useState({});
  const [systemKeys, setSystemKeys] = useState({ delete: "", edit_unlock: "" });
  const [systemDefaults, setSystemDefaults] = useState({ delete: "CEO@24", edit_unlock: "CEO@0240" });
  const [processes, setProcesses] = useState([]);
  const [showOpKey, setShowOpKey] = useState({});
  const [showSysDel, setShowSysDel] = useState(false);
  const [showSysEdit, setShowSysEdit] = useState(false);
  // Item Master access password — optional. If set, the Item Master page
  // prompts before render. Empty value clears the gate.
  const [itemMasterPwd, setItemMasterPwd] = useState("");
  const [itemMasterPwdSet, setItemMasterPwdSet] = useState(false);
  const [showImPwd, setShowImPwd] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const [p, pr, sk, im] = await Promise.all([
        axios.get(`${API}/settings/passwords`),
        axios.get(`${API}/processes`),
        axios.get(`${API}/settings/system-passkeys`),
        axios.get(`${API}/settings/item-master-password`),
      ]);
      setPasswords(p.data.passwords || {});
      setProcesses(pr.data || []);
      const pk = sk.data.passkeys || {};
      setSystemKeys({ delete: pk.delete || "", edit_unlock: pk.edit_unlock || "" });
      setSystemDefaults({ delete: pk.delete || "CEO@24", edit_unlock: pk.edit_unlock || "CEO@0240" });
      setItemMasterPwdSet(!!im.data?.required);
      setItemMasterPwd("");
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const saveOp = async () => {
    try { await axios.put(`${API}/settings/passwords`, passwords); toast.success("Operation passkeys updated"); }
    catch { toast.error("Failed"); }
  };

  const saveItemMasterPwd = async () => {
    try {
      const res = await axios.put(`${API}/settings/item-master-password`, { password: itemMasterPwd });
      toast.success(res.data.message || "Saved");
      // Clear local view of any cached unlock so the gate re-applies
      try { sessionStorage.removeItem("im_unlocked"); } catch {/* ignore */}
      setItemMasterPwd("");
      setItemMasterPwdSet(!!res.data.required);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveSystem = async () => {
    try {
      await axios.put(`${API}/settings/system-passkeys`, systemKeys);
      toast.success("System passkeys updated");
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;

  const opPasswordRows = processes.filter(p => p.requires_password);
  const configured = opPasswordRows.filter(p => passwords[p.code] && String(passwords[p.code]).trim()).length;

  // Unified snapshot of every configured passkey across the app.
  // Sections are ordered by priority: System > Item Master > Operations.
  // Values are read-only here — edits happen in the section-specific
  // cards below so the existing save flow stays intact.
  const summaryRows = [
    { section: "System",       label: "Delete Confirmation",     value: systemKeys.delete       || systemDefaults.delete,       fallback: !systemKeys.delete       ? "default" : "" },
    { section: "System",       label: "Edit Unlock",             value: systemKeys.edit_unlock  || systemDefaults.edit_unlock,  fallback: !systemKeys.edit_unlock  ? "default" : "" },
    { section: "Item Master",  label: "Item Master Access",      value: itemMasterPwdSet ? "•••••••• (active)" : "",           fallback: itemMasterPwdSet ? "" : "disabled" },
    ...opPasswordRows.map(op => ({
      section: "Operation",
      label: op.name || op.code,
      value: passwords[op.code] || "",
      fallback: (passwords[op.code] || "").trim() ? "" : "not set",
    })),
  ];

  return (
    <div className="space-y-5" data-testid="passkeys-panel">
      {/* PASSWORDS AT A GLANCE — read-only table listing every configured passkey
         across sections. Edits happen in the cards further down. */}
      <PasswordsSummary rows={summaryRows} />

      {/* ITEM MASTER ACCESS — optional per-tenant password to view Item Master */}
      <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="item-master-passkey-card">
        <div className="bg-[#0F766E] px-5 py-3 flex items-center gap-2">
          <Lock size={18} className="text-white" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Item Master Access Password</h2>
          <span className="ml-auto text-[11px] text-[#CCFBF1] italic">
            {itemMasterPwdSet ? "Active" : "Disabled (open access)"} · Optional
          </span>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[11px] text-[#475569]">
            If set, the Item Master page prompts for this password each session before showing any data.
            Leave blank and Save to <b>disable</b> the gate.
          </p>
          <div className="flex gap-2 items-center max-w-md">
            <Input type={showImPwd ? "text" : "password"}
              value={itemMasterPwd}
              onChange={e => setItemMasterPwd(e.target.value)}
              placeholder={itemMasterPwdSet ? "•••••••• (enter new value to change, or blank to clear)" : "Enter new password to enable"}
              className="min-h-[40px] font-mono"
              data-testid="item-master-passkey-input" />
            <Button type="button" variant="outline" onClick={() => setShowImPwd(s => !s)} className="text-xs">
              {showImPwd ? "Hide" : "Show"}
            </Button>
          </div>
          <Button onClick={saveItemMasterPwd}
            className="bg-[#0F766E] hover:bg-[#0D5F58] text-white text-xs font-bold"
            data-testid="save-item-master-passkey-btn">
            <Check size={14} className="mr-1" /> {itemMasterPwd ? "Save Password" : (itemMasterPwdSet ? "Clear Password" : "Save")}
          </Button>
        </div>
      </Card>

      {/* SYSTEM PASSKEYS — affect destructive / past-period actions */}
      <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="system-passkeys-card">
        <div className="bg-[#7C3AED] px-5 py-3 flex items-center gap-2">
          <Gear size={18} className="text-white" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">System Passkeys</h2>
          <span className="ml-auto text-[11px] text-[#EDE9FE] italic">Used for destructive & historical actions across the app.</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="border border-[#E2E8F0] rounded-lg p-4 bg-[#FEF2F2]" data-testid="passkey-delete-row">
            <Label className="text-[12px] font-bold flex items-center gap-2 text-[#991B1B]">
              <Trash size={14} /> Delete Passkey
            </Label>
            <p className="text-[11px] text-[#7F1D1D] mt-1 mb-2">
              Required when permanently deleting a transaction or wiping the entire Sales Register.
            </p>
            <div className="flex gap-2 items-center">
              <Input type={showSysDel ? "text" : "password"}
                value={systemKeys.delete}
                onChange={e => setSystemKeys({ ...systemKeys, delete: e.target.value })}
                placeholder={`Default: ${systemDefaults.delete} — leave blank to keep default`}
                className="min-h-[40px] font-mono"
                data-testid="passkey-delete-input" />
              <Button type="button" variant="outline" onClick={() => setShowSysDel(s => !s)} className="text-xs">
                {showSysDel ? "Hide" : "Show"}
              </Button>
            </div>
          </div>

          <div className="border border-[#E2E8F0] rounded-lg p-4 bg-[#FEF3C7]" data-testid="passkey-edit-row">
            <Label className="text-[12px] font-bold flex items-center gap-2 text-[#92400E]">
              <Lock size={14} /> Edit-Past-Period Passkey
            </Label>
            <p className="text-[11px] text-[#78350F] mt-1 mb-2">
              Required when editing transactions in a locked / past month-year (anything other than the current period).
            </p>
            <div className="flex gap-2 items-center">
              <Input type={showSysEdit ? "text" : "password"}
                value={systemKeys.edit_unlock}
                onChange={e => setSystemKeys({ ...systemKeys, edit_unlock: e.target.value })}
                placeholder={`Default: ${systemDefaults.edit_unlock} — leave blank to keep default`}
                className="min-h-[40px] font-mono"
                data-testid="passkey-edit-input" />
              <Button type="button" variant="outline" onClick={() => setShowSysEdit(s => !s)} className="text-xs">
                {showSysEdit ? "Hide" : "Show"}
              </Button>
            </div>
          </div>

          <Button onClick={saveSystem} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-bold" data-testid="save-system-passkeys-btn">
            <Check size={14} className="mr-1" /> Save System Passkeys
          </Button>
        </div>
      </Card>

      {/* ── RESET DATA — DESTRUCTIVE — wipes every transaction & RM movement
            BUT preserves Item Master / processes / suppliers / customers /
            users / settings. Auto-downloads a full backup first. */}
      <ResetDataCard />

      {/* OPERATION PASSKEYS — gate access to specific operation pages */}
      <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="passwords-panel">
        <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
          <Gear size={18} className="text-[#F5DF4D]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Operation Passkeys</h2>
          <span className="ml-auto text-[11px] text-[#CBD5E1] italic">
            {configured}/{opPasswordRows.length} configured · Leave blank to disable the gate.
          </span>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {opPasswordRows.map(p => {
            const v = passwords[p.code] ?? "";
            const open = !(v && String(v).trim());
            const visible = !!showOpKey[p.code];
            return (
              <div key={p.code} className="border border-[#E2E8F0] rounded-lg p-3" data-testid={`pwd-${p.code}`}>
                <Label className="text-[11px] font-semibold flex items-center justify-between">
                  <span>{p.name}</span>
                  {open ? (
                    <span className="text-[10px] text-[#059669] font-bold">OPEN</span>
                  ) : (
                    <button type="button" onClick={() => setShowOpKey(s => ({ ...s, [p.code]: !s[p.code] }))} className="text-[10px] text-[#3B82F6]">
                      {visible ? "hide" : "show"}
                    </button>
                  )}
                </Label>
                <Input type={visible ? "text" : "password"}
                  value={v}
                  onChange={e => setPasswords({ ...passwords, [p.code]: e.target.value })}
                  className="min-h-[36px] text-sm font-mono" placeholder="Leave blank = no password" />
              </div>
            );
          })}
        </div>
        <div className="px-5 pb-5">
          <Button onClick={saveOp} className="bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold" data-testid="save-passwords-btn">
            <Check size={14} className="mr-1" /> Save Operation Passkeys
          </Button>
        </div>
      </Card>
    </div>
  );
}



/* ── Read-only snapshot of every configured passkey. ─────────────────────── */
function PasswordsSummary({ rows }) {
  const [showAll, setShowAll] = useState(false);
  const mask = (s) => (s || "").length ? "••••••" : "";
  const bySection = rows.reduce((acc, r) => {
    (acc[r.section] = acc[r.section] || []).push(r);
    return acc;
  }, {});
  const sectionOrder = ["System", "Item Master", "Operation"];

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="passkeys-summary-card">
      <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
        <Lock size={18} className="text-white" />
        <h2 className="text-sm font-bold text-white uppercase tracking-wider">All Passwords At A Glance</h2>
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="ml-auto text-[11px] font-bold text-[#1D2951] bg-[#F5DF4D] hover:bg-[#F0D030] px-3 py-1 rounded"
          data-testid="passkeys-summary-toggle"
        >
          {showAll ? "Hide Values" : "Reveal Values"}
        </button>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-[11px] text-[#64748B]">
          Every configured passkey in one place. Use the section cards below to change any individual password.
          Defaults are shown when a passkey hasn&apos;t been customised for this tenant.
        </p>
        <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9] text-[#1D2951]">
              <tr>
                <th className="px-3 py-2 text-left font-semibold w-32">Section</th>
                <th className="px-3 py-2 text-left font-semibold">Passkey</th>
                <th className="px-3 py-2 text-left font-semibold">Value</th>
                <th className="px-3 py-2 text-left font-semibold w-32">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {sectionOrder.filter(s => bySection[s]?.length).map(section => (
                bySection[section].map((r, i) => (
                  <tr key={`${section}-${i}`} data-testid={`passkey-row-${section}-${i}`}>
                    {i === 0 && (
                      <td className="px-3 py-2 font-bold text-[#1D2951] align-top" rowSpan={bySection[section].length}>
                        {section}
                      </td>
                    )}
                    <td className="px-3 py-2 text-[#334155]">{r.label}</td>
                    <td className="px-3 py-2 font-mono">
                      {r.value ? (showAll ? r.value : mask(r.value)) : <span className="text-[#94A3B8] italic">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {r.fallback === "default" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#FEF3C7] text-[#B45309]">Default</span>
                      )}
                      {r.fallback === "disabled" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">Disabled</span>
                      )}
                      {r.fallback === "not set" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C]">Not set</span>
                      )}
                      {!r.fallback && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#DCFCE7] text-[#166534]">Custom</span>
                      )}
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}


/* ──────────────────────────── Backup & Storage ──────────────────────────── */

function BackupPanel() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState("");
  const [lastExport, setLastExport] = useState(null); // {counts: {parts: N, ...}, total: N, ts: '...'}
  const fileRef = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/storage/stats`, { skipPeriod: true });
      setStats(r.data);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const exportBackup = async () => {
    setBusy("export");
    try {
      const res = await axios.get(`${API}/backup/export`, {
        responseType: "blob",
        skipPeriod: true,
      });
      const blob = new Blob([res.data], { type: "application/gzip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `swami_backup_${stamp}.json.gz`;
      a.click();
      URL.revokeObjectURL(url);

      // Read per-collection counts from response headers (set by backend)
      let counts = null;
      let total = 0;
      try {
        const raw = res.headers["x-collection-counts"] || res.headers["X-Collection-Counts"];
        if (raw) counts = JSON.parse(raw);
        total = parseInt(res.headers["x-total-documents"] || res.headers["X-Total-Documents"] || "0", 10);
      } catch { /* headers missing — fall back to file size only */ }

      setLastExport({
        counts: counts || {},
        total,
        ts: new Date().toLocaleString(),
        size_kb: (blob.size / 1024).toFixed(1),
      });
      const partsN = (counts && counts.parts) || 0;
      toast.success(`Backup downloaded · ${total} docs total · Item Master: ${partsN}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setBusy(""); }
  };

  const [lastImport, setLastImport] = useState(null); // {restored, errors, ts}

  const importBackup = async (file) => {
    if (!file) return;
    if (!window.confirm(`This will REPLACE all data for this company with the contents of "${file.name}". Continue?`)) return;
    setBusy("import");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/backup/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        skipPeriod: true,
      });
      const s = res.data.summary || {};
      const restored = s.restored || {};
      const errors = s.errors || [];
      setLastImport({
        restored,
        errors,
        ts: new Date().toLocaleString(),
        imported_from: res.data.imported_from,
      });
      const restoredCount = Object.keys(restored).length;
      const errCount = errors.length;
      if (errCount === 0) {
        toast.success(`Restored ${restoredCount} collection(s) successfully`);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.warning(`Restored ${restoredCount} collection(s), ${errCount} had issues — see details below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const totalKB = stats?.total_kb ?? 0;
  const totalMB = stats?.total_mb ?? 0;
  const clusterMB = stats?.cluster_mb ?? 0;
  const collCount = stats?.collection_count ?? 0;

  const [recomputeBusy, setRecomputeBusy] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState(null);
  const recomputeAllStock = async () => {
    if (!window.confirm(
      "Re-derive total_stock_nos / balanced_qty_produced / carry_forward_balance for EVERY part+operation chain.\n\n" +
      "Safe to run anytime. Use this when some parts show total_stock = 0 after a backup restore or data migration."
    )) return;
    setRecomputeBusy(true);
    try {
      const res = await axios.post(`${API}/transactions/recompute-all-stock`, {}, { skipPeriod: true });
      setRecomputeResult({
        combos: res.data.combos,
        recomputed: res.data.recomputed,
        errors: res.data.errors || [],
        ts: new Date().toLocaleString(),
      });
      const errN = (res.data.errors || []).length;
      if (errN === 0) {
        toast.success(`Recomputed ${res.data.recomputed} stock chain(s)`);
      } else {
        toast.warning(`Recomputed ${res.data.recomputed} chain(s); ${errN} error(s) — see details below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputeBusy(false);
    }
  };

  const [resyncBusy, setResyncBusy] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);
  const resyncDispatchAllocations = async () => {
    if (!window.confirm(
      "Re-sync every Sales Register PO row's `dispatch_qty_2` to match the actual dispatch transactions, FIFO by oldest delivery date.\n\n" +
      "Use this when Sales Register / Daily Review Sheet show different dispatched totals than the Operation Transaction History. Idempotent — safe to run anytime."
    )) return;
    setResyncBusy(true);
    try {
      const res = await axios.post(`${API}/sales-register/resync-dispatch-allocations`, {}, { skipPeriod: true });
      setResyncResult({
        parts: res.data.parts_processed,
        rows: res.data.total_rows_allocated,
        ts: new Date().toLocaleString(),
      });
      toast.success(`Re-synced ${res.data.parts_processed} part(s) · ${res.data.total_rows_allocated} PO row(s) updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-sync failed");
    } finally {
      setResyncBusy(false);
    }
  };

  // Show KB when <1MB so small installs don't look empty
  const primaryLabel = totalMB >= 1
    ? `${totalMB.toFixed(2)} MB`
    : `${totalKB.toFixed(1)} KB`;

  // Soft plan cap — same threshold the deployment quota uses.
  const QUOTA_MB = 500;
  const usagePct = Math.min(100, (totalMB / QUOTA_MB) * 100);
  const danger = usagePct >= 80;
  const warn = usagePct >= 60;

  return (
    <div className="space-y-4" data-testid="backup-panel">
      {/* Storage % card */}
      <Card className={`border ${danger ? 'border-[#DC2626] bg-[#FEF2F2]' : warn ? 'border-[#F59E0B] bg-[#FFFBEB]' : 'border-[#E2E8F0] bg-white'} p-4`} data-testid="storage-tile">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] text-[#64748B] uppercase font-bold tracking-widest">Storage Used (this company)</p>
            <p className="text-xl sm:text-2xl font-black font-mono mt-0.5" style={{ color: danger ? '#DC2626' : warn ? '#D97706' : '#1D2951' }}>
              {primaryLabel}
              <span className="text-xs text-[#64748B] font-normal ml-1">/ {QUOTA_MB} MB plan</span>
            </p>
            <p className="text-[10px] text-[#94A3B8] mt-0.5">{collCount} collections · cluster total {clusterMB} MB</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-black font-mono" style={{ color: danger ? '#DC2626' : warn ? '#D97706' : '#059669' }}>
              {usagePct.toFixed(1)}%
            </p>
          </div>
        </div>
        <div className="mt-3 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${usagePct}%`, background: danger ? '#DC2626' : warn ? '#F59E0B' : '#10B981' }} />
        </div>
        {danger && <p className="text-xs text-[#DC2626] font-bold mt-2">⚠ Approaching plan limit. Archive old transactions or upgrade.</p>}
      </Card>

      <Card className="border border-[#E2E8F0] overflow-hidden">
        <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
          <BuildingOffice size={18} className="text-[#F5DF4D]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Backup & Restore</h2>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-[#475569]">
            Export a single compressed file containing every transaction, part, RM record,
            and configuration for this company. Import the same file later to restore
            the entire state. Other companies&apos; data is never included.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button onClick={exportBackup} disabled={!!busy}
              className="bg-[#1D2951] hover:bg-[#2A3B73] text-[#F5DF4D] font-bold h-11" data-testid="export-backup-btn">
              {busy === "export" ? "Exporting…" : "Export Backup (.json.gz)"}
            </Button>
            <Button variant="outline" disabled={!!busy}
              onClick={() => fileRef.current?.click()}
              className="h-11 border-[#1D2951] text-[#1D2951]" data-testid="import-backup-btn">
              {busy === "import" ? "Restoring…" : "Import Backup"}
            </Button>
            <input
              ref={fileRef} type="file" accept=".gz,.json" className="hidden"
              onChange={(e) => importBackup(e.target.files?.[0])}
              data-testid="backup-file-input"
            />
          </div>

          {lastExport && (
            <div className="border border-[#10B981] bg-[#ECFDF5] rounded p-3" data-testid="last-export-summary">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-[#10B981]" />
                <h3 className="text-xs font-bold text-[#065F46] uppercase tracking-wide">Last Export · {lastExport.ts}</h3>
                <span className="ml-auto text-[10px] font-mono text-[#065F46]">{lastExport.size_kb} KB · {lastExport.total} docs</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-[11px]">
                {Object.entries(lastExport.counts || {}).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
                  <div key={name} className={`flex justify-between px-2 py-1 rounded ${n > 0 ? 'bg-white' : 'bg-[#F8FAFC] text-[#94A3B8]'}`} data-testid={`exp-count-${name}`}>
                    <span className={n > 0 ? 'text-[#475569] font-semibold' : ''}>{name === 'parts' ? 'parts (Item Master)' : name}</span>
                    <span className={`font-mono font-bold ${n > 0 ? 'text-[#065F46]' : 'text-[#94A3B8]'}`}>{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {lastImport && (
            <div className={`border rounded p-3 ${(lastImport.errors || []).length === 0 ? 'border-[#10B981] bg-[#ECFDF5]' : 'border-[#F59E0B] bg-[#FFFBEB]'}`}
              data-testid="last-import-summary">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${(lastImport.errors || []).length === 0 ? 'bg-[#10B981]' : 'bg-[#F59E0B]'}`} />
                <h3 className={`text-xs font-bold uppercase tracking-wide ${(lastImport.errors || []).length === 0 ? 'text-[#065F46]' : 'text-[#92400E]'}`}>
                  Last Import · {lastImport.ts}
                </h3>
                <span className="ml-auto text-[10px] font-mono text-[#64748B]">from {lastImport.imported_from || "?"}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-[11px]">
                {Object.entries(lastImport.restored || {}).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
                  <div key={name} className="flex justify-between bg-white px-2 py-1 rounded">
                    <span className="text-[#475569] font-semibold">{name === 'parts' ? 'parts (Item Master)' : name}</span>
                    <span className="font-mono font-bold text-[#065F46]">{n}</span>
                  </div>
                ))}
              </div>
              {(lastImport.errors || []).length > 0 && (
                <div className="mt-2 border-t border-[#F59E0B] pt-2 space-y-1">
                  <div className="text-[11px] font-bold text-[#7C2D12]">⚠ Errors ({lastImport.errors.length})</div>
                  {lastImport.errors.map((e, i) => (
                    <div key={i} className="text-[10px] bg-white px-2 py-1 rounded font-mono">
                      <div className="font-bold text-[#7C2D12]">{e.collection}{e.row_failure_count ? ` · ${e.row_failure_count} row(s)` : ""}</div>
                      <div className="text-[#475569] break-all">{e.bulk_error || e.error}</div>
                      {e.row_failures && e.row_failures.length > 0 && (
                        <div className="mt-0.5 pl-2">
                          {e.row_failures.map((rf, j) => (
                            <div key={j} className="text-[#92400E]">↳ <code>{rf.id}</code>: {rf.error}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {stats && Object.keys(stats.collections || {}).length > 0 && (
            <div className="border-t border-[#E2E8F0] pt-3">
              <h3 className="text-xs font-bold text-[#475569] uppercase tracking-wide mb-2">Storage breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {Object.entries(stats.collections).sort((a, b) => b[1].bytes - a[1].bytes).map(([name, info]) => (
                  <div key={name} className="flex justify-between bg-[#F8FAFC] px-2 py-1 rounded">
                    <span className="text-[#475569]">{name}</span>
                    <span className="font-mono font-bold text-[#1D2951]">{info.mb} MB</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Recompute Stock Chains ───────────────────────────────────────── */}
          <div className="border-t border-[#E2E8F0] pt-3 mt-2">
            <div className="border border-[#F59E0B] bg-[#FFFBEB] rounded p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-xs font-bold text-[#92400E] uppercase tracking-wide">Recompute Stock Chains</h3>
                  <p className="text-[11px] text-[#7C2D12] mt-1">
                    Use this when some parts show <strong>Total Stock = 0</strong> even though they have opening
                    balance or receipts. Rebuilds <code>total_stock_nos</code>, <code>balanced_qty_produced</code> &
                    <code> carry_forward_balance</code> for every (part, operation) chain. Safe & idempotent.
                  </p>
                </div>
                <Button onClick={recomputeAllStock} disabled={recomputeBusy}
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold h-10 px-4 whitespace-nowrap"
                  data-testid="recompute-stock-btn">
                  {recomputeBusy ? "Recomputing…" : "Recompute All Stock"}
                </Button>
              </div>
              {recomputeResult && (
                <div className="mt-3 bg-white border border-[#FCD34D] rounded p-2 text-[11px]" data-testid="recompute-result">
                  <div className="font-bold text-[#92400E] mb-1">Last run · {recomputeResult.ts}</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-[#F8FAFC] px-2 py-1 rounded flex justify-between"><span className="text-[#475569]">Combos scanned</span><span className="font-mono font-bold">{recomputeResult.combos}</span></div>
                    <div className="bg-[#F8FAFC] px-2 py-1 rounded flex justify-between"><span className="text-[#475569]">Chains recomputed</span><span className="font-mono font-bold text-[#059669]">{recomputeResult.recomputed}</span></div>
                    <div className="bg-[#F8FAFC] px-2 py-1 rounded flex justify-between"><span className="text-[#475569]">Errors</span><span className={`font-mono font-bold ${recomputeResult.errors.length ? 'text-[#DC2626]' : 'text-[#059669]'}`}>{recomputeResult.errors.length}</span></div>
                  </div>
                  {recomputeResult.errors.length > 0 && (
                    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                      {recomputeResult.errors.slice(0, 10).map((e, i) => (
                        <div key={i} className="text-[10px] text-[#7C2D12] font-mono break-all">
                          part:{e.part_id?.slice(0,8)} op:{e.operation} → {e.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Re-sync Dispatch Allocations ─────────────────────────────────── */}
          <div className="border-t border-[#E2E8F0] pt-3 mt-2">
            <div className="border border-[#3B82F6] bg-[#EFF6FF] rounded p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-xs font-bold text-[#1E40AF] uppercase tracking-wide">Re-sync Dispatch Allocations</h3>
                  <p className="text-[11px] text-[#1E3A8A] mt-1">
                    Use when <strong>Sales Register / Daily Review Sheet</strong> show different dispatched totals than the
                    Operation Transaction History. Walks every part&apos;s dispatch transactions and FIFO-allocates them to
                    the Sales Register PO rows (oldest delivery date first). Idempotent.
                  </p>
                </div>
                <Button onClick={resyncDispatchAllocations} disabled={resyncBusy}
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold h-10 px-4 whitespace-nowrap"
                  data-testid="resync-dispatch-btn">
                  {resyncBusy ? "Re-syncing…" : "Re-sync Dispatch"}
                </Button>
              </div>
              {resyncResult && (
                <div className="mt-3 bg-white border border-[#93C5FD] rounded p-2 text-[11px]" data-testid="resync-result">
                  <div className="font-bold text-[#1E40AF] mb-1">Last run · {resyncResult.ts}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-[#F8FAFC] px-2 py-1 rounded flex justify-between"><span className="text-[#475569]">Parts processed</span><span className="font-mono font-bold text-[#1E40AF]">{resyncResult.parts}</span></div>
                    <div className="bg-[#F8FAFC] px-2 py-1 rounded flex justify-between"><span className="text-[#475569]">PO rows updated</span><span className="font-mono font-bold text-[#059669]">{resyncResult.rows}</span></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}


/* ──────────────────────────── Shared small Field ──────────────────────────── */

function Field({ label, value, onChange, testid, placeholder, type }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold">{label}</Label>
      <Input type={type || "text"} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="min-h-[36px] text-sm" data-testid={testid} />
    </div>
  );
}


/* ──────────────────────────── Outsource Processes ──────────────────────────── */

function OutsourceProcessesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ code: "", name: "" });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/outsource-processes`, { skipPeriod: true });
      setItems(r.data?.items || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persist = async (next) => {
    try {
      const payload = next.map(i => ({ code: i.code, name: i.name }));
      const r = await axios.put(`${API}/outsource-processes`, payload, { skipPeriod: true });
      setItems(r.data?.items || next);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const add = () => {
    if (!draft.code.trim() || !draft.name.trim()) { toast.error("Code & name required"); return; }
    const code = draft.code.trim().toLowerCase().replace(/\s+/g, "_");
    if (items.some(i => i.code === code)) { toast.error("Code already exists"); return; }
    const next = [...items, { code, name: draft.name.trim() }];
    setDraft({ code: "", name: "" });
    persist(next);
  };

  const remove = (code) => {
    if (!window.confirm(`Remove '${code}'?`)) return;
    persist(items.filter(i => i.code !== code));
  };

  const rename = (code, newName) => {
    persist(items.map(i => i.code === code ? { ...i, name: newName } : i));
  };

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="outsource-procs-panel">
      <div className="bg-[#1D2951] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShareNetwork size={18} className="text-[#F5DF4D]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Outsource Processes</h2>
          <span className="bg-[#F5DF4D] text-[#1D2951] text-xs font-bold rounded-full px-2 py-0.5">{items.length}</span>
        </div>
        <span className="text-[11px] text-[#CBD5E1] italic">These appear in the Outsource process dropdowns app-wide.</span>
      </div>
      <div className="p-4 grid grid-cols-[1fr_2fr_auto] gap-3 items-end bg-[#F0FDF4] border-b border-[#BBF7D0]">
        <Field label="Code" value={draft.code} onChange={v => setDraft({ ...draft, code: v })} testid="osp-code" placeholder="e.g. powder_coating" />
        <Field label="Display Name" value={draft.name} onChange={v => setDraft({ ...draft, name: v })} testid="osp-name" placeholder="e.g. Powder Coating" />
        <Button onClick={add} className="bg-[#059669] hover:bg-[#047857] text-white h-9 px-4 text-xs font-bold" data-testid="osp-add">
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F1F5F9] text-xs uppercase tracking-wider text-[#64748B]">
              <th className="px-4 py-2 text-left">Code</th>
              <th className="px-4 py-2 text-left">Display Name</th>
              <th className="px-4 py-2 text-center w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="3" className="text-center py-6 text-[#94A3B8] text-xs italic">No outsource processes — add one to enable Outsource flow.</td></tr>
            ) : items.map(i => (
              <tr key={i.code} className="border-b border-[#E2E8F0]" data-testid={`osp-row-${i.code}`}>
                <td className="px-4 py-2 font-mono text-xs text-[#1D2951]">{i.code}</td>
                <td className="px-4 py-2">
                  <Input defaultValue={i.name} onBlur={e => { if (e.target.value !== i.name) rename(i.code, e.target.value); }}
                    className="min-h-[32px] text-sm" data-testid={`osp-name-${i.code}`} />
                </td>
                <td className="px-4 py-2 text-center">
                  <Button size="sm" variant="outline" onClick={() => remove(i.code)}
                    className="h-7 px-2 text-xs text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`osp-del-${i.code}`}>
                    <Trash size={12} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


/* ──────────────────────────── Users ──────────────────────────── */

function UsersPanel() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", role: "Worker" });
  const [editing, setEditing] = useState(null); // user object
  const [editForm, setEditForm] = useState({ username: "", password: "", role: "" });

  // ── Special Feature Access (SFA) — sidebar-section-per-user gating ──
  const SFA_PASSWORD = "SFA2026";
  const [sfaUnlocked, setSfaUnlocked] = useState(false);
  const [sfaPwdOpen, setSfaPwdOpen] = useState(false);
  const [sfaPwdInput, setSfaPwdInput] = useState("");
  const [sectionsModal, setSectionsModal] = useState(null); // {user, selected:Set}
  const { processes } = useProcesses();

  const STATIC_SECTION_GROUPS = [
    { group: "Overview", items: [
      { key: "dashboard", label: "Dashboard" },
      { key: "sales-register", label: "Sales Register" },
      { key: "production-planning", label: "Production Planning" },
    ]},
    { group: "Raw Material", items: [
      { key: "raw-material", label: "RM Entry" },
      { key: "rm-planning", label: "RM Planning" },
      { key: "rm-opening-stock", label: "RM Opening Stock" },
    ]},
    { group: "Reports", items: [
      { key: "final-report", label: "Final Report" },
      { key: "work-order", label: "Work Order" },
      { key: "daily-review", label: "Daily Review Sheet" },
      { key: "inventory", label: "Inventory" },
      { key: "suppliers", label: "Suppliers" },
      { key: "stock-at-supplier", label: "Stock at Supplier" },
    ]},
    { group: "Costing", items: [
      { key: "cost-sheet", label: "Cost Sheet" },
      { key: "budget-schedule", label: "Budget as per Schedule" },
      { key: "performance-dispatch", label: "Performance as per Dispatch" },
    ]},
    { group: "Quality", items: [
      { key: "customer-complaints", label: "Customer Complaints" },
      { key: "quality-dashboard", label: "Quality Dashboard" },
    ]},
    { group: "Masters", items: [
      { key: "part-master", label: "Part Master" },
      { key: "bom-master", label: "BOM Master" },
      { key: "process-master", label: "Process Master" },
      { key: "customer-master", label: "Customer Master" },
      { key: "company-master", label: "Company Master" },
      { key: "machine-master", label: "Machine Master" },
      { key: "bom-routing", label: "BOM Routing" },
      { key: "bought-out", label: "Bought Out" },
      { key: "defect-master", label: "Defect Master" },
    ]},
  ];
  const OPERATION_SECTIONS = (processes || []).map(p => ({
    key: `operations/${p.code}`,
    label: p.name || p.code,
  }));
  const ALL_SECTION_GROUPS = [
    ...STATIC_SECTION_GROUPS.slice(0, 2),
    { group: "Operations / Processes", items: OPERATION_SECTIONS },
    ...STATIC_SECTION_GROUPS.slice(2),
  ];

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([
        axios.get(`${API}/auth/users`, { skipPeriod: true }),
        axios.get(`${API}/roles`, { skipPeriod: true }),
      ]);
      setRows(u.data || []);
      setRoles(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.username.trim() || !form.password.trim()) { toast.error("Username and password required"); return; }
    if (form.password.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    try {
      await axios.post(`${API}/auth/users`, form, { skipPeriod: true });
      toast.success("User created");
      setAdding(false);
      setForm({ username: "", password: "", role: "Worker" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (u) => {
    setEditing(u);
    setEditForm({ username: u.username, password: "", role: u.role });
  };

  const saveEdit = async () => {
    const payload = {};
    if (editForm.username && editForm.username !== editing.username) payload.username = editForm.username;
    if (editForm.password) payload.password = editForm.password;
    if (editForm.role && editForm.role !== editing.role) payload.role = editForm.role;
    if (Object.keys(payload).length === 0) { setEditing(null); return; }
    try {
      await axios.put(`${API}/auth/users/${editing.id}`, payload, { skipPeriod: true });
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user '${u.username}'?`)) return;
    try {
      await axios.delete(`${API}/auth/users/${u.id}`, { skipPeriod: true });
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitSfaPassword = () => {
    if (sfaPwdInput === SFA_PASSWORD) {
      setSfaUnlocked(true);
      setSfaPwdOpen(false);
      setSfaPwdInput("");
      toast.success("Special Feature Access unlocked");
    } else {
      toast.error("Wrong password");
    }
  };
  const openSectionsFor = (u) => {
    setSectionsModal({
      user: u,
      selected: new Set(Array.isArray(u.allowed_sections) ? u.allowed_sections : []),
    });
  };
  const toggleSectionKey = (k) => {
    setSectionsModal(m => {
      const s = new Set(m.selected);
      if (s.has(k)) s.delete(k); else s.add(k);
      return { ...m, selected: s };
    });
  };
  const saveSections = async () => {
    if (!sectionsModal) return;
    const list = Array.from(sectionsModal.selected);
    try {
      await axios.put(`${API}/auth/users/${sectionsModal.user.id}`,
        { allowed_sections: list }, { skipPeriod: true });
      toast.success(`Sections saved for '${sectionsModal.user.username}'`);
      setSectionsModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };
  const clearSections = async () => {
    if (!sectionsModal) return;
    if (!window.confirm(`Give '${sectionsModal.user.username}' access to EVERYTHING (clear restrictions)?`)) return;
    try {
      await axios.put(`${API}/auth/users/${sectionsModal.user.id}`,
        { allowed_sections: [] }, { skipPeriod: true });
      toast.success("Restrictions cleared — user sees everything");
      setSectionsModal(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="users-panel">
      <div className="bg-[#1D2951] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UsersIcon size={18} className="text-[#F5DF4D]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Users (this company)</h2>
          <span className="bg-[#F5DF4D] text-[#1D2951] text-xs font-bold rounded-full px-2 py-0.5">{rows.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm"
            onClick={() => sfaUnlocked ? setSfaUnlocked(false) : setSfaPwdOpen(true)}
            className={`text-xs font-bold gap-1 h-8 ${sfaUnlocked ? "bg-[#059669] hover:bg-[#047857] text-white" : "bg-[#B45309] hover:bg-[#92400E] text-white"}`}
            data-testid="sfa-toggle-btn">
            {sfaUnlocked
              ? <><LockOpen size={14} /> Special Access · Unlocked</>
              : <><LockKey size={14} /> Access Special Features</>}
          </Button>
          <Button size="sm" onClick={() => setAdding(!adding)}
            className="bg-[#F5DF4D] hover:bg-[#EAD63A] text-[#1D2951] text-xs font-bold gap-1 h-8" data-testid="add-user-btn">
            <Plus size={14} /> Add User
          </Button>
        </div>
      </div>
      {adding && (
        <div className="p-4 bg-[#F0FDF4] border-b border-[#BBF7D0] grid grid-cols-[1fr_1fr_1fr_auto_auto] gap-3 items-end" data-testid="add-user-form">
          <Field label="Username" value={form.username} onChange={v => setForm({ ...form, username: v })} testid="user-username" placeholder="e.g. quality_lead" />
          <Field label="Password" type="password" value={form.password} onChange={v => setForm({ ...form, password: v })} testid="user-password" placeholder="min 4 chars" />
          <div>
            <Label className="text-[11px] font-semibold">Role</Label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              data-testid="user-role"
              className="w-full min-h-[36px] text-sm border border-[#E2E8F0] rounded px-2">
              {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
          </div>
          <Button onClick={create} className="bg-[#059669] hover:bg-[#047857] text-white h-9 px-4 text-xs font-bold" data-testid="save-user-btn">
            <Check size={14} className="mr-1" /> Create
          </Button>
          <Button variant="outline" onClick={() => setAdding(false)} className="h-9 px-3"><X size={14} /></Button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F1F5F9] text-xs uppercase tracking-wider text-[#64748B]">
              <th className="px-4 py-2 text-left">Username</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-left">Created</th>
              {sfaUnlocked && <th className="px-4 py-2 text-left">Sections Access</th>}
              <th className="px-4 py-2 text-center w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={sfaUnlocked ? 5 : 4} className="text-center py-6 text-[#94A3B8] text-xs italic">No users yet — add one to grant login access.</td></tr>
            ) : rows.map(u => (
              <tr key={u.id} className="border-b border-[#E2E8F0]" data-testid={`user-row-${u.username}`}>
                <td className="px-4 py-2 font-bold text-[#1D2951]">{u.username}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                    u.role === "Creator" ? "bg-[#FEF3C7] text-[#92400E]" :
                    u.role === "Admin" ? "bg-[#DBEAFE] text-[#1E40AF]" :
                    "bg-[#F1F5F9] text-[#475569]"
                  }`}>{u.role}</span>
                </td>
                <td className="px-4 py-2 text-xs text-[#64748B]">{(u.created_at || "").slice(0, 10)}</td>
                {sfaUnlocked && (
                  <td className="px-4 py-2">
                    {u.role === "Creator" || u.role === "Admin" ? (
                      <span className="text-[10px] text-[#64748B] italic">Full access (role)</span>
                    ) : Array.isArray(u.allowed_sections) && u.allowed_sections.length > 0 ? (
                      <button onClick={() => openSectionsFor(u)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E40AF] text-[11px] font-bold hover:bg-[#DBEAFE]"
                        data-testid={`sections-${u.username}`}>
                        <ShieldCheck size={12} /> {u.allowed_sections.length} section{u.allowed_sections.length > 1 ? 's' : ''} · Edit
                      </button>
                    ) : (
                      <button onClick={() => openSectionsFor(u)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#F0FDF4] border border-[#BBF7D0] text-[#166534] text-[11px] font-bold hover:bg-[#DCFCE7]"
                        data-testid={`sections-${u.username}`}>
                        <LockOpen size={12} /> Full · Restrict
                      </button>
                    )}
                  </td>
                )}
                <td className="px-4 py-2 text-center flex gap-1 justify-center">
                  <Button size="sm" variant="outline" onClick={() => startEdit(u)} className="h-7 px-2 text-xs" data-testid={`edit-user-${u.username}`}>
                    <PencilSimple size={12} />
                  </Button>
                  {u.role !== "Creator" && (
                    <Button size="sm" variant="outline" onClick={() => remove(u)}
                      className="h-7 px-2 text-xs text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`del-user-${u.username}`}>
                      <Trash size={12} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <Card className="w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="edit-user-modal">
            <h3 className="text-base font-bold text-[#1D2951]">Edit user — {editing.username}</h3>
            <Field label="Username" value={editForm.username} onChange={v => setEditForm({ ...editForm, username: v })} testid="edit-user-username" />
            <Field label="New Password (leave blank to keep)" type="password" value={editForm.password} onChange={v => setEditForm({ ...editForm, password: v })} testid="edit-user-password" />
            <div>
              <Label className="text-[11px] font-semibold">Role</Label>
              <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                className="w-full min-h-[36px] text-sm border border-[#E2E8F0] rounded px-2"
                data-testid="edit-user-role">
                {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={saveEdit} className="flex-1 bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold" data-testid="save-edit-user-btn">
                <Check size={14} className="mr-1" /> Save
              </Button>
              <Button onClick={() => setEditing(null)} variant="outline" className="flex-1 text-xs">Cancel</Button>
            </div>
          </Card>
        </div>
      )}

      {/* ── Special Feature Access · password gate ── */}
      <Dialog open={sfaPwdOpen} onOpenChange={setSfaPwdOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#B45309]">
              <LockKey size={20} /> Special Feature Access
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#64748B] -mt-2">
            Enter the special-access password to unlock the ability to assign
            specific sidebar sections to non-admin users.
          </p>
          <Input type="password" value={sfaPwdInput}
            onChange={e => setSfaPwdInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submitSfaPassword()}
            placeholder="Password" className="min-h-[40px]" autoFocus
            data-testid="sfa-password-input" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSfaPwdOpen(false)} className="h-9">Cancel</Button>
            <Button onClick={submitSfaPassword}
              className="bg-[#B45309] hover:bg-[#92400E] text-white h-9"
              data-testid="sfa-password-submit">
              <LockOpen size={14} className="mr-1" /> Unlock
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Sections picker · per-user ── */}
      <Dialog open={!!sectionsModal} onOpenChange={(o) => !o && setSectionsModal(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-[#B45309]" />
              Sections Access — <span className="text-[#1D2951]">{sectionsModal?.user?.username}</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#64748B] -mt-2">
            Tick the sections this user should see in their sidebar. Untick to hide.
            Press <b>Give Full Access</b> to remove all restrictions.
          </p>
          {sectionsModal && (
            <div className="space-y-4">
              {ALL_SECTION_GROUPS.map(g => (
                <div key={g.group} className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                  <div className="bg-[#F1F5F9] px-3 py-1.5 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#1D2951]">{g.group}</span>
                    <div className="flex gap-2">
                      <button className="text-[10px] text-[#059669] font-bold hover:underline"
                        onClick={() => setSectionsModal(m => {
                          const s = new Set(m.selected);
                          g.items.forEach(it => s.add(it.key));
                          return { ...m, selected: s };
                        })}>Select all</button>
                      <button className="text-[10px] text-[#DC2626] font-bold hover:underline"
                        onClick={() => setSectionsModal(m => {
                          const s = new Set(m.selected);
                          g.items.forEach(it => s.delete(it.key));
                          return { ...m, selected: s };
                        })}>Clear</button>
                    </div>
                  </div>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {g.items.length === 0 && <span className="text-[11px] text-[#94A3B8] italic col-span-3">No items configured yet</span>}
                    {g.items.map(it => {
                      const on = sectionsModal.selected.has(it.key);
                      return (
                        <label key={it.key}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer text-xs ${on ? 'bg-[#EFF6FF] border-[#BFDBFE] text-[#1E40AF]' : 'bg-white border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC]'}`}
                          data-testid={`section-chip-${it.key}`}>
                          <input type="checkbox" checked={on}
                            onChange={() => toggleSectionKey(it.key)} className="accent-[#1D2951]" />
                          <span className="truncate font-semibold">{it.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between gap-2 pt-2 border-t border-[#E2E8F0] mt-4">
            <Button variant="outline" onClick={clearSections} className="h-9 text-[#059669] border-[#059669]">
              <LockOpen size={14} className="mr-1" /> Give Full Access
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSectionsModal(null)} className="h-9">Cancel</Button>
              <Button onClick={saveSections}
                className="bg-[#1D2951] hover:bg-[#2A3B73] text-white h-9"
                data-testid="sections-save">
                <Check size={14} className="mr-1" /> Save Access
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ────────────────────── Reset Data card ──────────────────────
// 3-step wizard:
//   1) Confirm modal: type password (CEO@2004)
//   2) On verify pass → auto-download backup
//   3) Call /reset-data → show summary toast
function ResetDataCard() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");  // "verifying" | "backup" | "wiping" | ""

  const reset = async () => {
    if (!password.trim()) { toast.error("Enter the reset password"); return; }
    setBusy(true);
    try {
      // Step 1 — verify
      setStep("verifying");
      await axios.post(`${API}/reset-data/verify`, { password: password.trim() });

      // Step 2 — auto-download backup
      setStep("backup");
      const backupRes = await axios.get(`${API}/backup/export`, { responseType: "blob" });
      const url = URL.createObjectURL(backupRes.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `Swami-Backup-PRE-RESET-${ts}.json.gz`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // tiny delay so the browser definitely flushes the download dialog
      await new Promise(r => setTimeout(r, 800));

      // Step 3 — actually wipe
      setStep("wiping");
      const res = await axios.post(`${API}/reset-data`, { password: password.trim(), confirm: true });
      const totalDeleted = Object.values(res.data.wiped || {})
        .filter(v => typeof v === "number")
        .reduce((s, n) => s + n, 0);
      toast.success(`Reset complete — ${totalDeleted} records wiped. Backup downloaded.`);
      setOpen(false); setPassword(""); setStep("");
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Reset failed";
      toast.error(msg);
    } finally {
      setBusy(false); setStep("");
    }
  };

  return (
    <>
      <Card className="border-2 border-[#FCA5A5] overflow-hidden" data-testid="reset-data-card">
        <div className="bg-[#7F1D1D] px-5 py-3 flex items-center gap-2">
          <Warning size={18} className="text-[#FCA5A5]" weight="bold" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Reset Data (Destructive)</h2>
          <span className="ml-auto text-[11px] text-[#FECACA] italic">Requires CEO password</span>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-[#475569] leading-relaxed">
            Wipes <b>every transaction</b> across all operations (Cutting → Dispatch), every Raw Material
            movement (receives, issues, pool openings), and every report sheet entry. <b>Preserves</b>:
            Item Master, RM Codes, Processes, Suppliers, Customers, Users, and Settings.
            A <b>full backup is automatically downloaded</b> before the wipe — keep it safe.
          </p>
          <Button onClick={() => setOpen(true)}
            className="bg-[#7F1D1D] hover:bg-[#991B1B] text-white text-xs font-bold"
            data-testid="open-reset-data-btn">
            <Warning size={14} className="mr-1" weight="bold" /> Reset Data…
          </Button>
        </div>
      </Card>

      {open && (
        <div className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3"
          onClick={() => !busy && setOpen(false)}
          data-testid="reset-data-modal">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl border-2 border-[#FCA5A5]"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 bg-[#7F1D1D] text-white flex items-center justify-between">
              <h3 className="font-black text-base flex items-center gap-2">
                <Warning size={18} weight="bold" /> Reset Data
              </h3>
              {!busy && (
                <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
              )}
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#7F1D1D] font-bold">⚠ This cannot be undone.</p>
              <p className="text-xs text-[#475569]">
                On confirm: (1) a full backup will be downloaded to your computer,
                (2) every transaction and RM movement will be wiped, (3) the page will reload.
                Item Master, suppliers, and settings stay intact.
              </p>
              <div>
                <Label className="text-xs font-semibold">Reset Password</Label>
                <Input type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter CEO password"
                  className="min-h-[40px] font-mono"
                  disabled={busy}
                  data-testid="reset-password-input" />
              </div>
              {step && (
                <div className="text-xs font-bold text-[#1D2951] flex items-center gap-2 p-2 bg-[#F8FAFC] rounded">
                  {step === "verifying" && "🔐 Verifying password..."}
                  {step === "backup" && <><DownloadSimple size={14} weight="bold" /> Downloading backup...</>}
                  {step === "wiping" && "🗑 Wiping transactional data..."}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="h-9 text-xs">
                  Cancel
                </Button>
                <Button onClick={reset} disabled={busy}
                  className="h-9 text-xs bg-[#7F1D1D] hover:bg-[#991B1B] text-white"
                  data-testid="confirm-reset-btn">
                  {busy ? "Working…" : "Backup & Reset"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



/* ────────────────────── Customer Cleanup ──────────────────────
   Groups near-duplicate customer names (from Part Master, Sales
   Register, Customer Master) by their first word (case-insensitive)
   so the user can pick a single canonical spelling per cluster and
   the system bulk-renames every reference. */
function CustomerCleanupPanel() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Per-group state: { [group_key]: { picked: string, custom: string, mode: "existing" | "custom" } }
  const [picks, setPicks] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/customize/customer-duplicates`);
      setGroups(r.data || []);
    } catch { toast.error("Failed to load duplicate scan"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setPickForGroup = (gk, patch) => {
    setPicks(prev => ({ ...prev, [gk]: { ...(prev[gk] || {}), ...patch } }));
  };

  const applyGroup = async (g) => {
    const pick = picks[g.group_key] || {};
    const canonical = (pick.mode === "custom" ? pick.custom : pick.picked || g.variants[0]?.name || "").trim();
    if (!canonical) { toast.error("Pick a canonical spelling first"); return; }
    const variants = g.variants.map(v => v.name).filter(v => v !== canonical);
    if (variants.length === 0) { toast("Nothing to merge — canonical is the only spelling", { icon: "ℹ️" }); return; }
    if (!window.confirm(`Rename ${variants.length} variant(s) to "${canonical}"?\n\n${variants.join("\n")}`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/customize/customer-canonicalize`, { canonical, variants });
      const u = r.data.updated || {};
      toast.success(`Merged into "${canonical}" — ${u.parts||0} parts, ${u.sales_register||0} sales rows, ${u.customer_masters||0} customer rows updated`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="customer-cleanup-panel">
      <div className="px-5 py-4 border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-black text-[#1D2951]">Customer Cleanup</h2>
            <p className="text-xs text-[#64748B] mt-0.5">
              Groups near-duplicate customer names by their first word across
              Part Master, Sales Register, and Customer Master. Pick the winning
              spelling and merge — every reference updates in one go.
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading || busy}
            className="h-9" data-testid="customer-cleanup-refresh">
            Rescan
          </Button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {loading && <div className="text-sm text-[#64748B]">Scanning…</div>}
        {!loading && groups.length === 0 && (
          <div className="text-center py-10 text-[#64748B]">
            <Check size={30} className="mx-auto text-emerald-500 mb-2" />
            <p className="text-sm font-semibold">No duplicate customer names found.</p>
            <p className="text-xs text-[#94A3B8] mt-1">Everything already matches consistently.</p>
          </div>
        )}
        {!loading && groups.map(g => {
          const pick = picks[g.group_key] || {};
          const activeMode = pick.mode || "existing";
          const activePicked = pick.picked || g.variants[0]?.name;
          return (
            <div key={g.group_key} className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`customer-group-${g.group_key}`}>
              <div className="px-4 py-3 bg-[#1D2951] text-white flex items-center justify-between">
                <span className="font-bold text-sm">
                  Group: <span className="font-mono uppercase">{g.group_key}</span>
                  <span className="ml-3 text-xs text-[#D4A843] font-normal">{g.variants.length} variants · {g.total_usage} references</span>
                </span>
                <Button
                  onClick={() => applyGroup(g)}
                  disabled={busy}
                  className="h-8 bg-[#D4A843] hover:bg-[#B8912E] text-[#1D2951] font-bold"
                  data-testid={`customer-merge-${g.group_key}`}
                >
                  Merge This Group
                </Button>
              </div>
              <div className="p-4 space-y-2">
                <p className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-bold">
                  Pick the canonical spelling
                </p>
                {g.variants.map(v => (
                  <label key={v.name}
                    className={`flex items-center gap-3 px-3 py-2 rounded border cursor-pointer transition-all ${
                      activeMode === "existing" && activePicked === v.name
                        ? "border-[#D4A843] bg-[#FFF9EB]"
                        : "border-[#E2E8F0] hover:bg-[#F8FAFC]"
                    }`}
                    data-testid={`customer-variant-${g.group_key}-${v.name}`}
                  >
                    <input
                      type="radio"
                      name={`group-${g.group_key}`}
                      checked={activeMode === "existing" && activePicked === v.name}
                      onChange={() => setPickForGroup(g.group_key, { mode: "existing", picked: v.name })}
                    />
                    <span className="flex-1 font-mono text-sm text-[#0F172A]">{v.name}</span>
                    <span className="text-[10px] text-[#94A3B8] font-mono">used {v.count}×</span>
                  </label>
                ))}
                <label
                  className={`flex items-center gap-3 px-3 py-2 rounded border cursor-pointer transition-all ${
                    activeMode === "custom" ? "border-[#D4A843] bg-[#FFF9EB]" : "border-dashed border-[#CBD5E1] hover:bg-[#F8FAFC]"
                  }`}
                >
                  <input
                    type="radio"
                    name={`group-${g.group_key}`}
                    checked={activeMode === "custom"}
                    onChange={() => setPickForGroup(g.group_key, { mode: "custom" })}
                  />
                  <span className="text-xs text-[#475569] font-semibold">Or type a custom spelling →</span>
                  <Input
                    value={pick.custom || ""}
                    onChange={e => setPickForGroup(g.group_key, { mode: "custom", custom: e.target.value })}
                    placeholder="e.g. CG India Pvt Ltd"
                    className="h-8 flex-1 max-w-xs font-mono text-sm"
                    data-testid={`customer-custom-${g.group_key}`}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}



/* ─────────────────────────────────────────────────────────────────
   Data Recovery Panel — Feb 27, 2026
   -----------------------------------------------------------------
   Rescue for the "invisible orphan" case: docs sitting in a tenant
   collection (e.g. cost_sheets) with a missing/empty/stale
   `company_id` field. Backend:
      GET  /api/admin/orphans/scan
      POST /api/admin/orphans/claim?password=MIGRATE@V2
───────────────────────────────────────────────────────────────── */
function DataRecoveryPanel() {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  const doScan = async () => {
    setBusy(true);
    setClaimResult(null);
    try {
      const r = await axios.get(`${API}/admin/orphans/scan`);
      setScan(r.data);
      const total = r.data?.grand_total_orphans || 0;
      if (total === 0) toast.success("No orphaned docs — everything is clean.");
      else toast(`Found ${total} orphan doc${total === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed — Creator/Admin role required");
    } finally { setBusy(false); }
  };

  const doClaim = async () => {
    if (!scan || (scan.grand_total_orphans || 0) === 0) {
      toast.error("Run a scan first"); return;
    }
    const total = scan.grand_total_orphans;
    if (!window.confirm(
      `Recover ${total} orphaned doc(s)?\n\n` +
      `This will assign them to your tenant (company_id="${scan.caller_company_id}") ` +
      `and (if v2 storage) copy them into the tenant-prefixed collection. ` +
      `Safe to re-run; idempotent.`
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/orphans/claim?password=${encodeURIComponent("MIGRATE@V2")}&dry_run=false`
      );
      setClaimResult(r.data);
      toast.success(
        `Recovered ${r.data.grand_total_claimed || 0} doc(s). ` +
        (r.data.grand_total_copied_to_v2
          ? `Copied ${r.data.grand_total_copied_to_v2} to v2.`
          : "")
      );
      await doScan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery failed");
    } finally { setBusy(false); }
  };

  const perColl = scan?.per_collection || {};
  const collectionsWithOrphans = Object.entries(perColl)
    .filter(([, v]) => (v?.orphans || 0) > 0)
    .sort((a, b) => (b[1].orphans || 0) - (a[1].orphans || 0));

  return (
    <Card className="border border-[#E2E8F0] p-5 space-y-4" data-testid="recovery-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center shrink-0">
          <Warning size={22} weight="fill" className="text-[#B45309]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-black text-[#1D2951]">Data Recovery — Orphan Documents</h3>
          <p className="text-xs text-[#64748B] mt-1 max-w-3xl">
            If a page shows an empty list even though the data still exists in the database, some
            documents may be missing the tenant marker (<span className="font-mono">company_id</span>).
            Run a Scan to see what&apos;s affected, then Recover to re-assign every orphan to your
            current tenant. The action is idempotent (safe to re-run).
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={doScan} disabled={busy}
          className="bg-[#1D2951] hover:bg-[#2A3B73] h-9"
          data-testid="recovery-scan-btn">
          {busy && !claimResult ? "Scanning…" : "1 · Scan for Orphans"}
        </Button>
        <Button onClick={doClaim}
          disabled={busy || !scan || (scan?.grand_total_orphans || 0) === 0}
          className="bg-[#B45309] hover:bg-[#92400E] h-9 text-white"
          data-testid="recovery-claim-btn">
          {busy && claimResult === null && scan ? "Recovering…" : "2 · Recover Now"}
        </Button>
      </div>

      {scan && (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <RecoveryStat label="Orphans Found" value={scan.grand_total_orphans || 0}
              tint={scan.grand_total_orphans > 0 ? "#B45309" : "#166534"} />
            <RecoveryStat label="Your Tenant" value={scan.caller_company_id || "—"} tint="#1D2951" mono />
            <RecoveryStat label="Storage Layout" value={scan.storage_layout || "—"} tint="#7C3AED" mono />
            <RecoveryStat label="Valid Tenants" value={(scan.valid_company_ids || []).length} tint="#0EA5E9" />
          </div>

          {collectionsWithOrphans.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
              <table className="w-full text-sm">
                <thead className="bg-[#F1F5F9] text-[#1D2951]">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Collection</th>
                    <th className="px-3 py-2 text-right font-semibold">Orphans</th>
                    <th className="px-3 py-2 text-left font-semibold">Sample IDs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {collectionsWithOrphans.map(([coll, info]) => (
                    <tr key={coll}>
                      <td className="px-3 py-2 font-mono font-bold text-[#0F172A]">{coll}</td>
                      <td className="px-3 py-2 text-right font-bold text-[#B45309]" data-testid={`orph-${coll}`}>
                        {info.orphans}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#64748B] font-mono">
                        {(info.sample || []).map(s => s.id).join(", ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {scan.grand_total_orphans === 0 && (
            <div className="p-3 rounded-md bg-[#DCFCE7] border border-[#86EFAC] text-sm text-[#166534] font-semibold">
              No orphan documents. Everything is properly tenant-scoped.
            </div>
          )}
        </div>
      )}

      {claimResult && (
        <div className="p-4 rounded-lg bg-[#F0FDF4] border-2 border-[#166534]">
          <div className="text-xs font-bold uppercase tracking-widest text-[#166534] mb-1">
            Recovery Complete
          </div>
          <div className="text-sm text-[#0F172A]">
            Recovered <b>{claimResult.grand_total_claimed || 0}</b> orphan doc(s) into
            tenant <span className="font-mono font-bold">{claimResult.claimed_to_company_id}</span>.
            {claimResult.grand_total_copied_to_v2 > 0 && (
              <> Copied <b>{claimResult.grand_total_copied_to_v2}</b> to v2 tenant-prefixed collections.</>
            )}
          </div>
          <p className="text-xs text-[#64748B] mt-1">Refresh any affected page (e.g. Cost Sheet) to see the restored records.</p>
        </div>
      )}
    </Card>
  );
}

function RecoveryStat({ label, value, tint, mono }) {
  return (
    <div className="p-3 rounded-lg border border-[#E2E8F0] bg-white">
      <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: tint }}>
        {label}
      </div>
      <div className={`mt-1 text-lg font-black text-[#0F172A] truncate ${mono ? "font-mono" : ""}`} title={String(value)}>
        {value}
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────
   RUN MTR — Move To (new) Reporting period
   -----------------------------------------------------------------
   Wipes every transaction / review / dispatch collection so a fresh
   reporting period starts clean, while preserving opening balances
   (RM Pool + RM Grade) and all master data (parts, BOM, customers…).
   Password-gated by `Swami@YYYY` derived at server side.
   Before wiping, the current RM Grade Stock closing balance is
   automatically rolled into the new opening.
───────────────────────────────────────────────────────────────── */
function RunMTRPanel() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [pwd, setPwd] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/mtr/status`, { skipPeriod: true }),
        axios.get(`${API}/mtr/history`, { skipPeriod: true }),
      ]);
      setStatus(s.data);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load MTR status");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doRun = async () => {
    if (!pwd.trim()) { toast.error("Password required"); return; }
    if (confirmText.trim().toUpperCase() !== "RUN MTR") {
      toast.error("Type RUN MTR to confirm"); return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mtr/run`, { password: pwd.trim(), note });
      toast.success(`MTR complete — cleared ${r.data.grand_total_cleared} doc(s), rolled ${r.data.rm_grade_rolled} grade(s)`);
      setPwd(""); setNote(""); setConfirmText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "MTR failed");
    } finally { setBusy(false); }
  };

  const totalToClear = status?.grand_total || 0;
  const yearExpected = status?.current_year;

  return (
    <Card className="border-2 border-[#B91C1C]/40 p-5 space-y-5" data-testid="mtr-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center shrink-0">
          <ArrowsClockwise size={22} weight="fill" className="text-[#B91C1C]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-black text-[#1D2951]">Run MTR — Move To Reporting Period</h3>
          <p className="text-xs text-[#64748B] mt-1 max-w-3xl">
            Wipes every transaction, sales dispatch, review sheet and work-order record so a new
            reporting period starts clean. RM Opening Stock, RM Grade Stock openings, cost sheets,
            part / BOM / customer masters and Customer Complaints are <b>preserved</b>. Before the
            wipe, RM Grade Stock&apos;s current closing balance is folded into the new opening
            (Closing = Opening + Received − Issued) — so nothing is silently lost.
          </p>
          <p className="text-[11px] text-[#B91C1C] font-bold mt-2">
            Irreversible action — take an Excel export first if you want a paper trail.
          </p>
        </div>
      </div>

      {status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <RecoveryStat label="Docs Marked for Delete" value={totalToClear}
            tint={totalToClear > 0 ? "#B91C1C" : "#166534"} />
          <RecoveryStat label="This Year" value={yearExpected || "—"} tint="#1D2951" mono />
          <RecoveryStat label="Access" value="Restricted" tint="#7C3AED" />
          <RecoveryStat label="Last Run" value={status.last_run?.ts ? new Date(status.last_run.ts).toLocaleDateString() : "Never"} tint="#0EA5E9" />
        </div>
      )}

      {status?.will_clear && (
        <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9] text-[#1D2951]">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Collection</th>
                <th className="px-3 py-2 text-right font-semibold">Rows to be Cleared</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {Object.entries(status.will_clear).map(([k, v]) => (
                <tr key={k}>
                  <td className="px-3 py-2 font-mono text-[#0F172A]">{k}</td>
                  <td className={`px-3 py-2 text-right font-bold ${v > 0 ? "text-[#B91C1C]" : "text-[#94A3B8]"}`}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Access Password *</Label>
          <Input type="password" value={pwd}
            onChange={e => setPwd(e.target.value)}
            placeholder="Enter access password"
            className="h-10 mt-1 font-mono" data-testid="mtr-pwd" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Note (optional)</Label>
          <Input value={note} onChange={e => setNote(e.target.value)}
            placeholder="e.g. FY 26-27 opening balance freeze"
            className="h-10 mt-1" data-testid="mtr-note" />
        </div>
      </div>

      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">
          Type <span className="text-[#B91C1C]">RUN MTR</span> to confirm *
        </Label>
        <Input value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder="RUN MTR"
          className="h-10 mt-1 font-mono uppercase" data-testid="mtr-confirm" />
      </div>

      <Button
        onClick={doRun}
        disabled={busy || !pwd.trim() || confirmText.trim().toUpperCase() !== "RUN MTR"}
        className="bg-[#B91C1C] hover:bg-[#7F1D1D] text-white h-11 w-full md:w-auto md:px-8"
        data-testid="mtr-run-btn"
      >
        {busy ? "Running…" : <><ArrowsClockwise size={16} className="mr-2" weight="bold" /> Run MTR Now</>}
      </Button>

      {history.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#64748B] mb-2">Recent Runs</div>
          <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
            <table className="w-full text-xs">
              <thead className="bg-[#F1F5F9] text-[#1D2951]">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">By</th>
                  <th className="px-3 py-2 text-right font-semibold">Cleared</th>
                  <th className="px-3 py-2 text-right font-semibold">Grades Rolled</th>
                  <th className="px-3 py-2 text-left font-semibold">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {history.slice(0, 20).map(h => (
                  <tr key={h.id}>
                    <td className="px-3 py-2 font-mono">{new Date(h.ts).toLocaleString()}</td>
                    <td className="px-3 py-2">{h.user}</td>
                    <td className="px-3 py-2 text-right font-bold text-[#B91C1C]">{h.grand_total}</td>
                    <td className="px-3 py-2 text-right font-mono">{h.rm_grade_rolled}</td>
                    <td className="px-3 py-2 text-[#64748B]">{h.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}



/* ─────────────────────────────────────────────────────────────
   Fix Negatives — July 2026 RCA follow-up
   ─────────────────────────────────────────────────────────────
   Historical rows that landed in Mongo with -ve balances (root
   cause: scroll-wheel / arrow-key mutation on number inputs)
   are surfaced here for review and one-click zero-out.
*/
function NegativeFixPanel() {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);

  const runScan = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/transactions/scan-negatives`);
      setScan(r.data);
      if (!r.data.count) toast.success("No negative-balance rows found — you're clean.");
    } catch (e) { toast.error("Scan failed: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const runFix = async () => {
    if (!scan?.count) return;
    if (!window.confirm(`Zero-out negatives on ${scan.count} transaction row(s) and recompute affected chains? This is irreversible.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/transactions/fix-negatives`);
      toast.success(r.data.message);
      setScan(null);
      await runScan();
    } catch (e) { toast.error("Fix failed: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="neg-fix-panel">
      <div>
        <h3 className="text-lg font-black text-[#1D2951]" style={{ fontFamily: "Chivo" }}>Fix Negative Balances</h3>
        <p className="text-xs text-[#64748B] mt-1 max-w-3xl">
          Scans every transaction for any negative numeric field (opening balance, produced qty,
          carry-forward balance, etc.). Zero-outs and recomputes the (part × operation) chain so
          subsequent months stop compounding the bad value.
        </p>
        <p className="text-[11px] text-[#7C2D12] mt-2 bg-[#FEF3C7] rounded p-2 max-w-3xl">
          <b>Root cause:</b> HTML number inputs allowed scroll-wheel and up/down arrow-key changes.
          A hover-scroll silently mutated a July value into negative. Both are now blocked app-wide
          — this panel exists to clean up rows already in the database.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={runScan} disabled={busy}
          variant="outline" className="gap-1 border-[#1D2951] text-[#1D2951]"
          data-testid="neg-fix-scan">
          <Warning size={14} /> {busy ? "Scanning…" : "Scan for Negatives"}
        </Button>
        {scan?.count > 0 && (
          <Button onClick={runFix} disabled={busy}
            className="bg-[#B91C1C] hover:bg-[#991B1B] text-white gap-1"
            data-testid="neg-fix-apply">
            Zero-out & Recompute ({scan.count})
          </Button>
        )}
      </div>

      {scan && (
        <div className="border border-[#E2E8F0] rounded overflow-hidden">
          <div className={`px-3 py-2 text-[11px] font-bold ${scan.count > 0 ? "bg-[#FEF3C7] text-[#78350F]" : "bg-[#DCFCE7] text-[#166534]"}`}>
            {scan.count > 0 ? `Found ${scan.count} row(s) with negative fields` : "All clean — no negatives found"}
          </div>
          {scan.count > 0 && (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-[11px]">
                <thead className="bg-[#F8FAFC] text-[#475569] text-[10px] uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Data ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Operation</th>
                    <th className="px-3 py-2 text-left font-semibold">Part No</th>
                    <th className="px-3 py-2 text-left font-semibold">Negative Fields</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {scan.rows.map((r) => (
                    <tr key={r.id} className="hover:bg-[#F8FAFC]">
                      <td className="px-3 py-1.5 font-mono">{r.date || "—"}</td>
                      <td className="px-3 py-1.5 font-mono">{r.data_id || "—"}</td>
                      <td className="px-3 py-1.5">{r.operation}</td>
                      <td className="px-3 py-1.5 font-mono">{r.part_no || r.part_id?.slice(0, 8)}</td>
                      <td className="px-3 py-1.5">
                        {Object.entries(r.negative_fields || {}).map(([k, v]) => (
                          <span key={k} className="inline-block bg-[#FCE7F3] text-[#9D174D] px-1.5 py-0.5 rounded mr-1 font-mono">
                            {k}: {v}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
